import {
  extractResponseMessage,
  extractResponseStatus,
  getCode,
} from '@utils/resolver'

const KTN_BIOGRAPHICS_MATCHERS = [
  { match: 'multiple members', code: 'MULTIPLE_MEMBERS' },
  { match: 'member not found', code: 'MEMBER_NOT_FOUND' },
  { match: 'ktndetails not found', code: 'KTN_NOT_FOUND' },
  { match: 'enrollment not found', code: 'ENROLLMENT_NOT_FOUND' },
  { match: 'missing phone number and email', code: 'MISSING_CONTACT_INFO' },
] as const
type KtnBiographicsErrorCode = typeof KTN_BIOGRAPHICS_MATCHERS[number]['code']

const KTN_APPLICATION_ID_MATCHERS = [
  { match: 'invalid application id', code: 'INVALID_APPLICATION_ID' },
  {
    match: 'no enrollment found for application',
    code: 'ENROLLMENT_NOT_FOUND',
  },
  { match: 'no ktn found for application', code: 'KTN_NOT_FOUND' },
  { match: 'missing phone number and email', code: 'MISSING_CONTACT_INFO' },
] as const
type KtnApplicationIdErrorCode =
  typeof KTN_APPLICATION_ID_MATCHERS[number]['code']

const KTN_VERIFIED_LOOKUP_MATCHERS = [
  { match: 'unverified, expired, or invalid', code: 'SESSION_EXPIRED' },
  { match: 'do not match', code: 'CONTACT_MISMATCH' },
  { match: 'enrollment not found', code: 'ENROLLMENT_NOT_FOUND' },
] as const
type KtnVerifiedLookupErrorCode =
  typeof KTN_VERIFIED_LOOKUP_MATCHERS[number]['code']

const KTN_APPLICATION_ID_VERIFIED_LOOKUP_MATCHERS = [
  { match: 'unverified, expired, or invalid', code: 'SESSION_EXPIRED' },
  { match: 'do not match', code: 'CONTACT_MISMATCH' },
  { match: 'invalid application id', code: 'INVALID_APPLICATION_ID' },
  {
    match: 'no enrollment found matching application',
    code: 'ENROLLMENT_NOT_FOUND',
  },
] as const
type KtnApplicationIdVerifiedLookupErrorCode =
  typeof KTN_APPLICATION_ID_VERIFIED_LOOKUP_MATCHERS[number]['code']

const STATUS_BIOGRAPHICS_MATCHERS = [
  { match: 'multiple members', code: 'MULTIPLE_MEMBERS' },
  { match: 'member not found', code: 'MEMBER_NOT_FOUND' },
  { match: 'enrollment not found', code: 'ENROLLMENT_NOT_FOUND' },
  { match: 'missing phone number and email', code: 'MISSING_CONTACT_INFO' },
] as const
type StatusBiographicsErrorCode =
  typeof STATUS_BIOGRAPHICS_MATCHERS[number]['code']

const STATUS_APPLICATION_ID_MATCHERS = [
  { match: 'invalid application id', code: 'INVALID_APPLICATION_ID' },
  {
    match: 'no enrollment found for application',
    code: 'ENROLLMENT_NOT_FOUND',
  },
  { match: 'missing phone number and email', code: 'MISSING_CONTACT_INFO' },
] as const
type StatusApplicationIdErrorCode =
  typeof STATUS_APPLICATION_ID_MATCHERS[number]['code']

const STATUS_VERIFIED_LOOKUP_MATCHERS = [
  { match: 'unverified, expired, or invalid', code: 'SESSION_EXPIRED' },
  { match: 'enrollment not found', code: 'ENROLLMENT_NOT_FOUND' },
] as const
type StatusVerifiedLookupErrorCode =
  typeof STATUS_VERIFIED_LOOKUP_MATCHERS[number]['code']

const STATUS_APPLICATION_ID_VERIFIED_LOOKUP_MATCHERS = [
  { match: 'unverified, expired, or invalid', code: 'SESSION_EXPIRED' },
  { match: 'invalid application id', code: 'INVALID_APPLICATION_ID' },
  { match: 'enrollment not found', code: 'ENROLLMENT_NOT_FOUND' },
] as const
type StatusApplicationIdVerifiedLookupErrorCode =
  typeof STATUS_APPLICATION_ID_VERIFIED_LOOKUP_MATCHERS[number]['code']

const OTP_SEND_MATCHERS = [
  {
    match: 'phone number or email address must be provided',
    code: 'NO_CONTACT_PROVIDED',
  },
  { match: 'only us (+1) phone numbers are accepted', code: 'NON_US_PHONE' },
] as const
type OtpSendErrorCode = typeof OTP_SEND_MATCHERS[number]['code']

const OTP_VERIFY_MATCHERS = [
  { match: 'invalid otp', code: 'INVALID_OTP' },
] as const
type OtpVerifyErrorCode = typeof OTP_VERIFY_MATCHERS[number]['code']

export type LookupErrorCode =
  | KtnBiographicsErrorCode
  | KtnApplicationIdErrorCode
  | KtnVerifiedLookupErrorCode
  | KtnApplicationIdVerifiedLookupErrorCode
  | StatusBiographicsErrorCode
  | StatusApplicationIdErrorCode
  | StatusVerifiedLookupErrorCode
  | StatusApplicationIdVerifiedLookupErrorCode
  | OtpSendErrorCode
  | OtpVerifyErrorCode

/** Resolve error from POST /updated-ktn-lookup/biographics */
async function resolveKtnBiographicsError(error: unknown) {
  try {
    const status = extractResponseStatus(error)
    if (status === 300) return 'MULTIPLE_MEMBERS'
    const message = await extractResponseMessage(error)
    return getCode(message, KTN_BIOGRAPHICS_MATCHERS, 'BIOGRAPHICS_UNKNOWN')
  } catch {
    return 'KTN_BIOGRAPHICS_EXCEPTION'
  }
}

/** Resolve error from POST /updated-ktn-lookup/applicationId/{applicationId} */
async function resolveKtnApplicationIdError(error: unknown) {
  try {
    const message = await extractResponseMessage(error)
    return getCode(
      message,
      KTN_APPLICATION_ID_MATCHERS,
      'APPLICATION_ID_UNKNOWN'
    )
  } catch {
    return 'KTN_APPLICATION_ID_EXCEPTION'
  }
}

/** Resolve error from POST /updated-ktn-lookup/session/{otpSessionId} (biographics path) */
async function resolveKtnVerifiedLookupError(error: unknown) {
  try {
    const message = await extractResponseMessage(error)
    return getCode(
      message,
      KTN_VERIFIED_LOOKUP_MATCHERS,
      'VERIFIED_LOOKUP_UNKNOWN'
    )
  } catch {
    return 'KTN_VERIFIED_LOOKUP_EXCEPTION'
  }
}

/** Resolve error from POST /updated-ktn-lookup/session/{otpSessionId}/applicationId/{applicationId} */
async function resolveKtnApplicationIdVerifiedLookupError(error: unknown) {
  try {
    const message = await extractResponseMessage(error)
    return getCode(
      message,
      KTN_APPLICATION_ID_VERIFIED_LOOKUP_MATCHERS,
      'VERIFIED_LOOKUP_UNKNOWN'
    )
  } catch {
    return 'KTN_APPLICATION_ID_VERIFIED_LOOKUP_EXCEPTION'
  }
}

/** Resolve error from POST /updated-applicant-status/biographics */
async function resolveStatusBiographicsError(error: unknown) {
  try {
    const status = extractResponseStatus(error)
    if (status === 300) return 'MULTIPLE_MEMBERS'
    const message = await extractResponseMessage(error)
    return getCode(message, STATUS_BIOGRAPHICS_MATCHERS, 'BIOGRAPHICS_UNKNOWN')
  } catch {
    return 'STATUS_BIOGRAPHICS_EXCEPTION'
  }
}

/** Resolve error from POST /updated-applicant-status/applicationId/{applicationId} */
async function resolveStatusApplicationIdError(error: unknown) {
  try {
    const message = await extractResponseMessage(error)
    return getCode(
      message,
      STATUS_APPLICATION_ID_MATCHERS,
      'APPLICATION_ID_UNKNOWN'
    )
  } catch {
    return 'STATUS_APPLICATION_ID_EXCEPTION'
  }
}

/** Resolve error from POST /updated-applicant-status/session/{otpSessionId} (biographics path) */
async function resolveStatusVerifiedLookupError(error: unknown) {
  try {
    const message = await extractResponseMessage(error)
    return getCode(
      message,
      STATUS_VERIFIED_LOOKUP_MATCHERS,
      'VERIFIED_LOOKUP_UNKNOWN'
    )
  } catch {
    return 'STATUS_VERIFIED_LOOKUP_EXCEPTION'
  }
}

/** Resolve error from POST /updated-applicant-status/session/{otpSessionId}/applicationId/{applicationId} */
async function resolveStatusApplicationIdVerifiedLookupError(error: unknown) {
  try {
    const message = await extractResponseMessage(error)
    return getCode(
      message,
      STATUS_APPLICATION_ID_VERIFIED_LOOKUP_MATCHERS,
      'VERIFIED_LOOKUP_UNKNOWN'
    )
  } catch {
    return 'STATUS_APPLICATION_ID_VERIFIED_LOOKUP_EXCEPTION'
  }
}

/** Resolve error from POST /updated-ktn-lookup/session (otpKtndetails — send OTP via comm method) */
async function resolveOtpSendError(error: unknown) {
  try {
    const message = await extractResponseMessage(error)
    return getCode(message, OTP_SEND_MATCHERS, 'OTP_SEND_UNKNOWN')
  } catch {
    return 'OTP_SEND_EXCEPTION'
  }
}

/** Resolve error from POST /otp_appstatus_challenge/session/{id}/code/{code} */
async function resolveOtpVerifyError(error: unknown) {
  try {
    const message = await extractResponseMessage(error)
    return getCode(message, OTP_VERIFY_MATCHERS, 'OTP_VERIFY_UNKNOWN')
  } catch {
    return 'OTP_VERIFY_EXCEPTION'
  }
}

export const resolveError = {
  ktn: {
    applicationId: resolveKtnApplicationIdError,
    applicationIdLookup: resolveKtnApplicationIdVerifiedLookupError,
    biographics: resolveKtnBiographicsError,
    biographicsLookup: resolveKtnVerifiedLookupError,
    sendOTP: resolveOtpSendError,
    verifyOTP: resolveOtpVerifyError,
  },
  status: {
    applicationId: resolveStatusApplicationIdError,
    applicationIdLookup: resolveStatusApplicationIdVerifiedLookupError,
    biographics: resolveStatusBiographicsError,
    biographicsLookup: resolveStatusVerifiedLookupError,
    verifyOTP: resolveOtpVerifyError,
  },
} as const
